<template>
    <div class="admin-login-container">
        <!-- Background Elements -->
        <div class="background-elements">
            <div class="floating-orb orb-1"></div>
            <div class="floating-orb orb-2"></div>
            <div class="floating-orb orb-3"></div>
            <div class="security-grid"></div>
        </div>

        <!-- Main Login Card -->
        <div class="admin-login-card">
            <!-- Header Section -->
            <div class="admin-header">
                <div class="admin-logo-container">
                    <div class="logo-glow"></div>
                    <router-link to="/" class="admin-logo-link">
                        <img src="/favicon.ico" alt="Admin Portal" class="admin-logo" />
                    </router-link>
                    <div class="admin-logo-ring"></div>
                    <div class="security-badge">
                        <i class="bi bi-shield-lock-fill"></i>
                    </div>
                </div>
                <h1 class="admin-title">ADMIN PORTAL</h1>
                <p class="admin-subtitle">Control Panel Access</p>
            </div>

            <!-- Login Form -->
            <form class="admin-login-form" @submit.prevent="handleSubmit">
                <!-- Username Field -->
                <div class="admin-input-group">
                    <label for="username" class="admin-input-label">
                        <i class="bi bi-person-badge-fill"></i>
                        Username
                    </label>
                    <div class="admin-input-container">
                        <input
                            type="text"
                            id="username"
                            v-model="username"
                            class="admin-input"
                            placeholder="Enter admin username"
                            required
                            autocomplete="username"
                        />
                    </div>
                </div>

                <!-- Password Field -->
                <div class="admin-input-group">
                    <label for="password" class="admin-input-label">
                        <i class="bi bi-key-fill"></i>
                        Password
                    </label>
                    <div class="admin-input-container">
                        <input
                            :type="passwordFieldType"
                            id="password"
                            v-model="password"
                            class="admin-input"
                            placeholder="Enter admin password"
                            required
                            autocomplete="current-password"
                        />
                        <button
                            type="button"
                            @click="togglePasswordVisibility"
                            class="admin-visibility-toggle"
                            :class="{ 'visible': passwordFieldType === 'text' }"
                        >
                            <i :class="passwordFieldType === 'password' ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                        </button>
                    </div>
                </div>

                <!-- Error Message -->
                <div v-if="errmsg" class="admin-error-message">
                    <i class="bi bi-exclamation-octagon-fill"></i>
                    <span>{{ errmsg }}</span>
                </div>

                <!-- Action Buttons -->
                <div class="admin-action-buttons">
                    <button
                        type="submit"
                        class="admin-login-btn"
                        :disabled="!username || !password"
                    >
                        <i class="bi bi-shield-lock"></i>
                        <span>Access Control Panel</span>
                        <div class="btn-glow"></div>
                    </button>
                    
                    <button
                        type="button"
                        @click.prevent="handleCancelClick"
                        class="admin-cancel-btn"
                    >
                        <i class="bi bi-arrow-left"></i>
                        <span>Back to Home</span>
                    </button>
                </div>
            </form>

            <!-- Security Footer -->
            <div class="admin-security-footer">
                <div class="security-info">
                    <div class="security-item">
                        <i class="bi bi-cpu-fill"></i>
                        <span>Secure Admin Access</span>
                    </div>
                    <div class="security-item">
                        <i class="bi bi-database-fill"></i>
                        <span>Encrypted Connection</span>
                    </div>
                </div>
                
                <div class="admin-powered-by">
                    <div class="company-logo">
                        <img src="/favicon.ico" alt="Exodous" />
                    </div>
                    <div class="company-info">
                        <span class="powered-text">Admin System</span>
                        <span class="company-name">Exodous I.T. Solutions</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Access Level Indicator -->
        <div class="access-level">
            <div class="level-indicator">
                <div class="level-dot"></div>
                <span>Administrator Access Level</span>
            </div>
        </div>
    </div>
</template>

<script>
import { ref } from "vue";
import { useStore } from "vuex";
import router from "@/router";

export default {
    name: "AdminLoginPage",
    setup() {
        const username = ref("");
        const password = ref("");
        const passwordFieldType = ref("password");
        const store = useStore();
        const errmsg = ref("");
        const returnURL = router.currentRoute.value.query.return_url || "/";
        const isSubmitting = ref(false);

        const togglePasswordVisibility = () => {
            passwordFieldType.value =
                passwordFieldType.value === "password" ? "text" : "password";
        };

        const handleSubmit = async () => {
            if (isSubmitting.value) return;
            
            try {
                isSubmitting.value = true;
                errmsg.value = "";
                await store.dispatch("auth/login", {
                    username: username.value,
                    password: password.value,
                });
                router.replace(returnURL);
            } catch (error) {
                console.error("Admin login error:", error);
                if (error.error) {
                    errmsg.value = error.error.message;
                } else {
                    errmsg.value = "Authentication server is unreachable.";
                }
                
                // Shake animation for error
                const button = document.querySelector('.admin-login-btn');
                button.classList.add('shake');
                setTimeout(() => button.classList.remove('shake'), 500);
            } finally {
                isSubmitting.value = false;
            }
        };

        const handleCancelClick = () => {
            router.replace("/");
        };

        return {
            username,
            password,
            passwordFieldType,
            togglePasswordVisibility,
            handleSubmit,
            handleCancelClick,
            errmsg,
            isSubmitting,
        };
    },
};
</script>

<style scoped>

/* Main Container */
.admin-login-container {
    min-height: 100vh;
    background: linear-gradient(135deg, var(--dark-bg) 0%, #1a1a1a 50%, #1c1200 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    position: relative;
    overflow: hidden;
    font-family: 'Poppins', sans-serif;
}

/* Background Elements */
.background-elements {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.floating-orb {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle, var(--gold-primary) 0%, transparent 70%);
    opacity: 0.1;
    animation: float 8s ease-in-out infinite;
}

.orb-1 {
    width: 150px;
    height: 150px;
    top: 20%;
    left: 10%;
    animation-delay: 0s;
}

.orb-2 {
    width: 100px;
    height: 100px;
    top: 60%;
    right: 15%;
    animation-delay: 2s;
}

.orb-3 {
    width: 80px;
    height: 80px;
    bottom: 30%;
    left: 20%;
    animation-delay: 4s;
}

.security-grid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
        linear-gradient(rgba(212, 175, 55, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(212, 175, 55, 0.03) 1px, transparent 1px);
    background-size: 50px 50px;
    animation: gridMove 20s linear infinite;
}

/* Admin Login Card */
.admin-login-card {
    background: rgba(26, 26, 26, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid var(--border-gold);
    border-radius: 20px;
    padding: 40px;
    width: 100%;
    max-width: 440px;
    box-shadow: 
        0 20px 60px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(212, 175, 55, 0.1),
        0 0 50px rgba(212, 175, 55, 0.1);
    position: relative;
    z-index: 2;
}

/* Header Section */
.admin-header {
    text-align: center;
    margin-bottom: 40px;
}

.admin-logo-container {
    position: relative;
    width: 120px;
    height: 120px;
    margin: 0 auto 25px;
}

.logo-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 140px;
    height: 140px;
    background: radial-gradient(circle, var(--gold-primary) 0%, transparent 70%);
    opacity: 0.2;
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
}

.admin-logo {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    border: 3px solid var(--border-gold);
    box-shadow: 0 0 30px var(--shadow-gold);
    transition: all 0.3s ease;
    position: relative;
    z-index: 2;
}

.admin-logo:hover {
    transform: scale(1.05);
    box-shadow: 0 0 40px var(--shadow-gold);
}

.admin-logo-ring {
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border: 2px solid transparent;
    border-radius: 50%;
    background: conic-gradient(from 0deg, var(--gold-primary), var(--gold-secondary), var(--gold-primary));
    opacity: 0.5;
    animation: rotate 10s linear infinite;
}

.security-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid var(--dark-bg);
    box-shadow: 0 0 10px var(--shadow-gold);
}

.security-badge i {
    color: var(--dark-bg);
    font-size: 0.8rem;
}

.admin-title {
    font-size: 2.2rem;
    font-weight: 800;
    background: linear-gradient(135deg, var(--text-gold) 0%, var(--gold-light) 50%, var(--gold-primary) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    text-shadow: 0 0 30px rgba(212, 175, 55, 0.5);
    margin-bottom: 8px;
    letter-spacing: 1.5px;
}

.admin-subtitle {
    color: var(--text-muted);
    font-size: 1rem;
    font-weight: 300;
    text-transform: uppercase;
    letter-spacing: 1px;
}

/* Login Form */
.admin-login-form {
    margin-bottom: 30px;
}

.admin-input-group {
    margin-bottom: 25px;
}

.admin-input-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-light);
    font-weight: 600;
    margin-bottom: 10px;
    font-size: 0.95rem;
}

.admin-input-label i {
    color: var(--gold-primary);
    font-size: 1rem;
}

.admin-input-container {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
}

.admin-input {
    width: 100%;
    padding: 15px 50px 15px 20px;
    background: rgba(0, 0, 0, 0.3);
    border: 2px solid var(--border-gold);
    border-radius: 12px;
    color: var(--text-light);
    font-size: 1rem;
    font-weight: 500;
    transition: all 0.3s ease;
    outline: none;
    box-sizing: border-box;
}

.admin-input:focus {
    border-color: var(--gold-primary);
    background: rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 20px var(--shadow-gold);
}

.admin-input::placeholder {
    color: var(--text-muted);
}

.admin-visibility-toggle {
    position: absolute;
    right: 15px;
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 5px;
    border-radius: 5px;
    transition: all 0.3s ease;
    z-index: 3;
}

.admin-visibility-toggle:hover {
    color: var(--gold-primary);
    background: rgba(212, 175, 55, 0.1);
}

.admin-visibility-toggle.visible {
    color: var(--gold-primary);
}

/* Error Message */
.admin-error-message {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(231, 76, 60, 0.1);
    border: 1px solid rgba(231, 76, 60, 0.3);
    color: var(--error-color);
    padding: 12px 15px;
    border-radius: 10px;
    margin-bottom: 20px;
    font-size: 0.9rem;
    font-weight: 500;
}

.admin-error-message i {
    font-size: 1rem;
}

/* Action Buttons */
.admin-action-buttons {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.admin-login-btn {
    position: relative;
    width: 100%;
    padding: 16px;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    border: none;
    border-radius: 12px;
    color: var(--dark-bg);
    font-size: 1.1rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

.admin-login-btn:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(212, 175, 55, 0.4);
}

.admin-login-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.btn-glow {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s ease;
}

.admin-login-btn:not(:disabled):hover .btn-glow {
    left: 100%;
}

.admin-cancel-btn {
    width: 100%;
    padding: 14px;
    background: transparent;
    border: 2px solid var(--border-gold);
    border-radius: 12px;
    color: var(--text-muted);
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.admin-cancel-btn:hover {
    background: rgba(212, 175, 55, 0.1);
    color: var(--gold-primary);
    border-color: var(--gold-primary);
}

/* Security Footer */
.admin-security-footer {
    border-top: 1px solid var(--border-gold);
    padding-top: 25px;
}

.security-info {
    display: flex;
    justify-content: space-around;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;
}

.security-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-muted);
    font-size: 0.8rem;
}

.security-item i {
    color: var(--gold-primary);
    font-size: 0.9rem;
}

.admin-powered-by {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
}

.company-logo {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid var(--border-gold);
    box-shadow: 0 0 8px var(--shadow-gold);
}

.company-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.company-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.powered-text {
    font-size: 0.7rem;
    color: var(--text-muted);
}

.company-name {
    font-weight: 600;
    color: var(--text-light);
    font-size: 0.8rem;
}

/* Access Level Indicator */
.access-level {
    position: absolute;
    bottom: 20px;
    left: 20px;
}

.level-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(0, 0, 0, 0.5);
    padding: 8px 15px;
    border-radius: 20px;
    border: 1px solid var(--border-gold);
    color: var(--text-muted);
    font-size: 0.8rem;
    backdrop-filter: blur(10px);
}

.level-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--gold-primary);
    animation: pulse 2s ease-in-out infinite;
}

/* Animations */
@keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-15px) rotate(180deg); }
}

@keyframes gridMove {
    0% { transform: translate(0, 0); }
    100% { transform: translate(50px, 50px); }
}

@keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.1); }
}

@keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
}

.shake {
    animation: shake 0.5s ease-in-out;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
    .admin-login-container {
        padding: 15px;
    }

    .admin-login-card {
        padding: 30px 25px;
    }

    .admin-logo-container {
        width: 100px;
        height: 100px;
    }

    .admin-logo {
        width: 100px;
        height: 100px;
    }

    .admin-title {
        font-size: 1.8rem;
    }

    .admin-subtitle {
        font-size: 0.9rem;
    }

    .admin-input {
        padding: 12px 45px 12px 15px;
        font-size: 0.95rem;
    }

    .admin-login-btn, .admin-cancel-btn {
        padding: 14px;
        font-size: 1rem;
    }

    .security-info {
        flex-direction: column;
        align-items: center;
        gap: 10px;
    }

    .access-level {
        bottom: 10px;
        left: 10px;
        right: 10px;
        text-align: center;
    }

    .orb-1, .orb-2, .orb-3 {
        display: none;
    }
}

@media (max-width: 480px) {
    .admin-login-card {
        padding: 25px 20px;
    }

    .admin-logo-container {
        width: 80px;
        height: 80px;
    }

    .admin-logo {
        width: 80px;
        height: 80px;
    }

    .admin-title {
        font-size: 1.6rem;
    }

    .admin-powered-by {
        flex-direction: column;
        gap: 8px;
    }
}
</style>